import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { getUser } from '@homeviews/store'
import { useSelector, useDispatch } from 'react-redux'
import Grid from '@material-ui/core/Grid'
import {
  useGetWidgetsQuery,
  useGetWidgetQuery,
  setWidgetsSort,
  selectActiveWidgetsSort,
  useDeleteWidgetMutation,
  isStandardDeveloperUser,
} from '@homeviews/store'
import type { ITheme } from '@homeviews/styles'
import {
  Table,
  Button,
  Typography,
  AccordionWrapper,
  Dialog,
  TextField,
  LockedContentHeader,
  InfoBanner,
  Card,
  WindowTitle,
} from '@homeviews/shared-ui'
import { createRows, headCells, createSkeletonRows } from '@homeviews/widgets'

import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { Widget } from '@homeviews/models'
import { Box } from '@material-ui/core'
import type { IWidgetType } from '@homeviews/widgets'
import { WIDGET_TYPES } from '@homeviews/widgets'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
    },
    confirmationIcon: {
      transform: 'translateY(2px)',
      marginRight: 6,
    },
    accordionDetails: {
      width: '100%',
    },
    noWidgetsContainer: {
      [theme.breakpoints.up('md')]: {
        flexWrap: 'nowrap',
      },
    },
    noWidgetsImage: {
      maxWidth: 459,
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: 459,
      },
    },
    noWidgetsMainCol: {
      maxWidth: 480,
    },
    noWidgetsParagraph: {
      color: theme.colors.grey600,
      marginBottom: 20,
      fontWeight: 400,
      fontSize: theme.typography.pxToRem(22),
      lineHeight: theme.typography.pxToRem(34),
      letterSpacing: '-0.22px',
    },
    noWidgetsCreateButton: {
      marginTop: 10,
    },
  }),
)

interface IActiveWidgetState {
  id?: number
  code?: string
}

interface IWidgetToDelete {
  id?: number
  confirmationText?: string
}

const skeletonRows = createSkeletonRows()

const Widgets: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const user = useSelector(getUser)
  const isStandardDeveloper = useSelector(isStandardDeveloperUser)
  const activeSort = useSelector(selectActiveWidgetsSort)

  const [widgetToDelete, setWidgetToDelete] = useState<IWidgetToDelete>({
    id: null,
    confirmationText: '',
  })
  const [activeWidget, setActiveWidget] = useState<IActiveWidgetState>({
    id: null,
    code: null,
  })

  const [deleteWidget, { isLoading: isDeleting }] = useDeleteWidgetMutation()

  const { data: response, isFetching } = useGetWidgetsQuery(
    { developer: user?.developer_id, sort: activeSort },
    {
      skip: !user?.developer_id,
    },
  )

  useGetWidgetQuery(
    { developer: user?.developer_id, id: activeWidget?.id },
    {
      skip: !activeWidget?.id,
    },
  )

  const widgets = response?.widgets || []

  const handleGetWidget = (id: number) => {
    setActiveWidget({
      ...activeWidget,
      id,
    })
  }

  const handleDeleteWidget = (id: number) => {
    setWidgetToDelete({
      ...widgetToDelete,
      id,
    })
  }

  const confirmDeleteWidget = async () => {
    try {
      await deleteWidget({
        developer: user?.developer_id,
        id: widgetToDelete?.id,
      }).unwrap()

      setWidgetToDelete({
        id: null,
        confirmationText: '',
      })
    } catch (err) {
      console.log(err)
    }
  }

  const rows = useMemo(
    () => createRows(widgets, handleGetWidget, handleDeleteWidget),
    [widgets],
  )

  const handleConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value
    setWidgetToDelete({
      ...widgetToDelete,
      confirmationText: value,
    })
  }

  const handleSortChange = (sort: any) => {
    if (sort?.forceValue) {
      dispatch(setWidgetsSort({ sort: sort?.forceValue }))
      return
    }
    dispatch(setWidgetsSort({ sort }))
  }

  const validateDeleteConfirmation = (
    source: 'button' | 'input' = 'button',
  ): boolean => {
    if (source === 'input' && !widgetToDelete?.confirmationText) {
      return true
    }
    if (
      widgetToDelete?.confirmationText &&
      widgetToDelete.confirmationText.toLowerCase() === 'delete'
    ) {
      return true
    }
    return false
  }

  return (
    <>
      <WindowTitle title="Widgets" />
      <div className={classes.root}>
        {isStandardDeveloper && (
          <InfoBanner theme="gold" upgrade>
            <Typography className="l-infoBanner__title">
              Upgrade your account today
            </Typography>
          </InfoBanner>
        )}
        <div className="l-pageHeader">
          <Typography variant="h1">Widgets</Typography>
          {!isStandardDeveloper && (
            <Link to="/widgets/create">
              <Button variant="contained" color="primary" customColor="primary">
                Create widget
              </Button>
            </Link>
          )}
        </div>
        {isStandardDeveloper && (
          <Card
            header={
              <LockedContentHeader isLocked={isStandardDeveloper}>
                <Typography variant="h3" className="l-lockedContent__title">
                  Create Widgets
                </Typography>
              </LockedContentHeader>
            }
          />
        )}
        {isMobile ? (
          <AccordionWrapper>
            {!!widgets?.length &&
              widgets.map((widget: Widget) => (
                  <Accordion key={widget.id}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography className="l-accordionHeading">
                        {widget.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                      <div className="c-accordionDetails">
                        <div className="c-accordionDetail">
                          <p className="c-accordionDetailLabel">Widget ID</p>
                          <p className="c-accordionDetailValue">{widget.id}</p>
                        </div>
                        <div className="c-accordionDetail">
                          <p className="c-accordionDetailLabel">Development</p>
                          <p className="c-accordionDetailValue">
                            {widget?.development?.name || ''}
                          </p>
                        </div>
                        <div className="c-accordionDetail">
                          <p className="c-accordionDetailLabel">Widget Type</p>
                          <p className="c-accordionDetailValue">
                            {WIDGET_TYPES.find(
                              (x: IWidgetType) => x.id === widget.widget_type,
                            )?.title || ''}
                          </p>
                        </div>
                        <div className="c-accordionDetail">
                          <p className="c-accordionDetailLabel">Publish URL</p>
                          <p className="c-accordionDetailValue">{widget.website}</p>
                        </div>
                        <div className="c-accordionActions">
                          <Button
                            variant="outlined"
                            customColor="red"
                            onClick={() => handleDeleteWidget(widget.id)}
                          >
                            Delete
                          </Button>
                          <Button
                            variant="outlined"
                            customVariant="outlinedPrimary"
                            fullWidth
                            onClick={() => handleGetWidget(widget.id)}
                          >
                            Preview
                          </Button>
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>
              ))}
          </AccordionWrapper>
        ) : (
          (isFetching || !!rows?.length) && (
            <Table
              headCells={headCells}
              rows={isFetching ? skeletonRows : rows}
              activeSort={activeSort}
              onSortChange={handleSortChange}
            />
          )
        )}
        {!widgets ||
          (!widgets?.length && !isFetching && (
            <Card
              header={
                <Typography variant="h3" className="l-lockedContent__title">
                  Why use widgets?
                </Typography>
              }
            >
              <Grid className={classes.noWidgetsContainer} container>
                <Grid item>
                  <img
                    src="assets/images/widget-preview@2x.png"
                    alt="Login background"
                    className={classes.noWidgetsImage}
                  />
                </Grid>
                <Grid item className={classes.noWidgetsMainCol}>
                  <Typography variant="h1">
                    If you&apos;ve got it, flaunt it
                  </Typography>
                  <Typography className={classes.noWidgetsParagraph}>
                    Displaying product reviews has been proven to significantly
                    increase conversion rates. We make it really easy to share
                    the great reviews you&apos;ve earnt, which will help qualify
                    your leads and increase your sales.
                  </Typography>
                  <Typography className={classes.noWidgetsParagraph}>
                    Create your first widget now.
                  </Typography>
                  {!isStandardDeveloper && (
                    <Link to="/widgets/create">
                      <Button
                        variant="contained"
                        color="primary"
                        customColor="primary"
                        className={classes.noWidgetsCreateButton}
                      >
                        Create widget
                      </Button>
                    </Link>
                  )}
                </Grid>
              </Grid>
            </Card>
          ))}
        <Dialog
          open={!!widgetToDelete?.id}
          dialogHeading={
            <Typography variant="h3">
              <img
                className={classes.confirmationIcon}
                src="assets/icons/medium/alert-red.svg"
                alt="Dialog icon"
              />
              Confirm deletion of widget
            </Typography>
          }
          dialogContent={
            <div>
              <Box marginBottom={2}>
                <Typography>
                  Widget may be in-use on a live website please check before
                  deleting.
                </Typography>
              </Box>
              <TextField
                label="Type the word delete below and click confirm"
                value={widgetToDelete.confirmationText}
                onChange={handleConfirmationTextChange}
                error={!validateDeleteConfirmation('input')}
              />
            </div>
          }
          dialogActions={
            <div>
              <Button
                variant="outlined"
                customVariant="outlinedPrimary"
                onClick={() => {
                  setWidgetToDelete({
                    id: null,
                    confirmationText: '',
                  })
                }}
              >
                Cancel without delete
              </Button>
              <Button
                variant="contained"
                customVariant="containedPrimary"
                onClick={() => {
                  void confirmDeleteWidget()
                }}
                disabled={!validateDeleteConfirmation()}
                isLoading={isDeleting}
              >
                Confirm
              </Button>
            </div>
          }
        />
      </div>
    </>
  )
}

export default Widgets
